/**
 * chiiki-delivery-banner.js
 * 印鑑ラボ 地域SEOブログの Hero 配送バナー（#js-delivery-widget）に
 * 「本日発送」「○月○日（曜）にお届け予定」の動的テキストを差し込む。
 *
 * 既存 assets/delivery-estimator.js と同じカットオフ・祝日ロジックを内包する。
 * 別ファイルとして提供する理由:
 *   - chiiki ページには独自の視覚デザイン（緑バナー + パルス）が既にあり、
 *     <delivery-estimator> の inline UI を流用するとデザインが破綻するため。
 *
 * 受け側:
 *   <div id="js-delivery-widget"></div>
 *   オプション属性:
 *     data-postal="803-0000"  あらかじめ郵便番号が判明している場合
 *
 * 描画結果:
 *   <div class="chiiki-delivery-status">… 本日発送 …</div>
 *   <div class="chiiki-delivery-detail">… 4月20日（月）にお届け予定 …</div>
 */
(function () {
  'use strict';

  var HOLIDAYS = new Set([
    '2025-01-01','2025-01-02','2025-01-03','2025-01-13','2025-02-11','2025-02-23','2025-02-24',
    '2025-03-20','2025-04-29','2025-05-03','2025-05-04','2025-05-05','2025-05-06','2025-07-21',
    '2025-08-11','2025-09-15','2025-09-23','2025-10-13','2025-11-03','2025-11-23','2025-11-24',
    '2026-01-01','2026-01-02','2026-01-03','2026-01-12','2026-02-11','2026-02-23','2026-03-20',
    '2026-04-29','2026-05-03','2026-05-04','2026-05-05','2026-05-06','2026-07-20','2026-08-11',
    '2026-09-21','2026-09-22','2026-09-23','2026-10-12','2026-11-03','2026-11-23',
    '2027-01-01','2027-01-02','2027-01-03','2027-01-11','2027-02-11','2027-02-23','2027-03-21',
    '2027-03-22','2027-04-29','2027-05-03','2027-05-04','2027-05-05','2027-07-19','2027-08-11',
    '2027-09-20','2027-09-23','2027-10-11','2027-11-03','2027-11-23'
  ]);

  var CUTOFF_HOUR = 16;
  var DAY_NAMES = ['日','月','火','水','木','金','土'];

  // 離島郵便番号テーブル（先頭3〜5桁 → 地名）
  // 注: 完全網羅ではなく、ヤマト運輸の主要離島カバレッジに合わせた最小集合。
  //     ユーザー報告で漏れがあれば追記する。
  var REMOTE_ISLAND_3 = {
    '684': '隠岐諸島（島前）',
    '685': '隠岐の島',
    '817': '対馬',
    '894': '奄美大島',
    '906': '宮古島',
    '907': '石垣島・八重山',
    '952': '佐渡'
  };
  var REMOTE_ISLAND_4 = {
    '1001': '伊豆諸島（三宅・御蔵・八丈・青ヶ島）',
    '1002': '小笠原諸島',
    '8115': '壱岐',
    '8530': '五島列島（福江島）',
    '8532': '五島列島（久賀島）',
    '8534': '五島列島（奈留島）',
    '8574': '新上五島町',
    '8913': '種子島',
    '8914': '屋久島',
    '8915': 'トカラ列島・三島村',
    '8917': '徳之島',
    '8918': '沖永良部島',
    '8919': '与論島',
    '8961': '甑島列島',
    '9013': '久米島・慶良間諸島'
  };
  var REMOTE_ISLAND_5 = {
    // 伊豆諸島（100-0xxx は千代田区本土と被るため5桁で判定）
    '10001': '伊豆大島',  // 100-0101〜0104
    '10002': '伊豆大島',  // 100-0211〜0214
    '10003': '利島',      // 100-0301
    '10004': '新島',      // 100-0401〜0402
    '10005': '式根島',    // 100-0511
    '10006': '神津島',    // 100-0601
    '90412': '伊江島',
    '90504': '伊是名島',
    '90505': '伊平屋島',
    '90506': '伊平屋島'
  };

  // 郵便番号正規化：全角→半角、ハイフン除去、数字のみ抽出
  function normalizeZip(zip) {
    if (!zip) return '';
    return String(zip).normalize('NFKC').replace(/\D/g, '');
  }

  // 離島判定。該当すれば地名を返す。それ以外は null。
  function lookupRemoteIsland(zip) {
    var z = normalizeZip(zip);
    if (z.length < 3) return null;
    if (z.length >= 5 && REMOTE_ISLAND_5[z.slice(0, 5)]) return REMOTE_ISLAND_5[z.slice(0, 5)];
    if (z.length >= 4 && REMOTE_ISLAND_4[z.slice(0, 4)]) return REMOTE_ISLAND_4[z.slice(0, 4)];
    if (REMOTE_ISLAND_3[z.slice(0, 3)]) return REMOTE_ISLAND_3[z.slice(0, 3)];
    return null;
  }

  function isRemoteIsland(zip) {
    return lookupRemoteIsland(zip) !== null;
  }

  // 配送プロファイル
  //   type: 'remote' | 'hokkaido' | 'okinawa' | 'main' | 'unknown'
  //   addDays: ship 日からの加算日数（remote/unknown は null）
  //   regionLabel: 地名（離島と北海道・沖縄本島で設定）
  function getDeliveryProfile(zip) {
    var z = normalizeZip(zip);
    if (z.length < 3) return { type: 'unknown', addDays: null, regionLabel: null };
    var remote = lookupRemoteIsland(z);
    if (remote) return { type: 'remote', addDays: null, regionLabel: remote };

    var p = parseInt(z.slice(0, 3), 10);
    // 北海道 (001-009 札幌, 040-099 函館以南)
    if ((p >= 1 && p <= 9) || (p >= 40 && p <= 99)) return { type: 'hokkaido', addDays: 2, regionLabel: '北海道' };
    // 沖縄本島 (900-905)。906/907 は上の REMOTE で除外済み。
    if (p >= 900 && p <= 905) return { type: 'okinawa', addDays: 2, regionLabel: '沖縄本島' };
    // それ以外（本州・四国・九州本土）翌日
    return { type: 'main', addDays: 1, regionLabel: null };
  }

  function toJSTDate(d) {
    return new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
  }

  function ymd(d) {
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var dd = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + m + '-' + dd;
  }

  function isHoliday(d) { return HOLIDAYS.has(ymd(d)); }
  function isWeekend(d) { var w = d.getDay(); return w === 0 || w === 6; }
  function isBusinessDay(d) { return !isHoliday(d) && !isWeekend(d); }

  function addDays(d, n) {
    var r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
  }

  function nextBusinessDay(d) {
    var r = new Date(d);
    while (!isBusinessDay(r)) r = addDays(r, 1);
    return r;
  }

  function getShippingDate(now) {
    var jst = toJSTDate(now);
    var hour = jst.getHours();
    if (hour < CUTOFF_HOUR && isBusinessDay(jst)) return jst;
    return nextBusinessDay(addDays(jst, 1));
  }

  function formatDisplay(d) {
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var dd = String(d.getDate()).padStart(2, '0');
    return m + '月' + dd + '日（' + DAY_NAMES[d.getDay()] + '）';
  }

  // buildText: 郵便番号(部分でも可・全角混在可)から発送・お届け文言を生成
  //   返り値: { greeting, shipping, delivery, profile }
  //     greeting: 「宮古島ですね。」のような地名挨拶（離島時のみ）。それ以外は ''
  //     shipping: 発送日（「16時までのご注文で本日発送」 or 「4月25日（土）に発送」）
  //     delivery: お届け予定または離島ガイダンス
  function buildText(zipFull) {
    var now = new Date();
    var ship = getShippingDate(now);
    var jst = toJSTDate(now);
    var isToday = ymd(ship) === ymd(jst);
    // 「16時までの注文」を本日発送のときだけ明示（15:59 表示で誤認させないため）
    var shipLabel = isToday
      ? '16時までのご注文で本日発送'
      : formatDisplay(ship) + 'に発送';

    var z = normalizeZip(zipFull);
    if (!z) {
      // 郵便番号未入力時はヒーローバナーの「最短17時間」訴求と整合させ、
      // 本土最短ケース（翌営業日着）を仮表示する。実際の入力時は profile に応じて上書き。
      var arrival0 = addDays(ship, 1);
      var defaultDelivery = formatDisplay(arrival0) + 'にお届け予定です。（最短17時間）';
      return { greeting: '', shipping: shipLabel, delivery: defaultDelivery, profile: { type: 'unknown', addDays: 1, regionLabel: null } };
    }

    var profile = getDeliveryProfile(z);
    if (profile.type === 'remote') {
      return {
        greeting: profile.regionLabel + 'ですね。',
        shipping: shipLabel,
        delivery: '配達までのお日にちについて、離島地域についてはヤマト運輸さまのサービスレベルに準じます。',
        profile: profile
      };
    }
    if (profile.type === 'unknown') {
      return { greeting: '', shipping: shipLabel, delivery: '', profile: profile };
    }
    var arrival = addDays(ship, profile.addDays);
    var arrivalLabel = formatDisplay(arrival) + 'にお届け予定です。';
    if (profile.type === 'main') arrivalLabel += '（最短17時間）';
    return { greeting: '', shipping: shipLabel, delivery: arrivalLabel, profile: profile };
  }

  function render(host) {
    var postal = host.getAttribute('data-postal');
    var text = buildText(postal || '');
    var detail = host.querySelector('[data-chiiki-delivery-detail]');
    // 旧: shipping 行 (「16時までのご注文で本日発送」or 「4月25日に発送」) は
    //     上段の dbanner__detail「16時までのご注文・決済確定で即日発送いたします（土日祝含む）。」と
    //     内容が重複して冗長なので非表示。お届け予定（detail）のみ残す。
    var oldStatus = host.querySelector('[data-chiiki-delivery-status]');
    if (oldStatus) oldStatus.remove();
    if (!detail) {
      host.innerHTML = '<div class="chiiki-delivery-detail" data-chiiki-delivery-detail></div>';
      detail = host.querySelector('[data-chiiki-delivery-detail]');
    }
    detail.textContent = text.delivery;
  }

  window.ChiikiDelivery = {
    buildText: buildText,
    isRemoteIsland: isRemoteIsland,
    lookupRemoteIsland: lookupRemoteIsland,
    getDeliveryProfile: getDeliveryProfile,
    formatDisplay: formatDisplay,
    getShippingDate: getShippingDate,
    normalizeZip: normalizeZip
  };

  function attach() {
    var host = document.getElementById('js-delivery-widget');
    if (!host) return;
    render(host);
    setInterval(function () { render(host); }, 60000);

    document.addEventListener('chiiki:postal-changed', function (event) {
      var detail = event && event.detail;
      if (!detail || typeof detail.postal !== 'string') return;
      host.setAttribute('data-postal', detail.postal);
      render(host);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();

/* ============================================================
 *  Chiiki LP コンシェルジュ Runtime (asset 経由ロード)
 *  Shopify の Liquid bytecode キャッシュを迂回するため、
 *  シナリオ駆動 + AI 自由質問のロジックを asset JS に同梱する。
 *  sync:2026-04-23T21:18 hero banner 「最短17時間」+ 郵便番号未入力でも到着予定日表示
 * ============================================================ */
(function(){
  'use strict';
  if (window.__chiikiConciergeBooted) return;
  window.__chiikiConciergeBooted = true;

  var FALLBACK_CFG = {
    supabaseUrl: 'https://tmqepjeieyqdvvizenlm.supabase.co',
    supabaseAnonKey: 'sb_publishable_NORctpD2w1LNaPCzZ5qU6g_pQQZRpL1',
    chatEndpoint: 'shopify-chat',
    productPageUrl: '/collections/all',
    pageContext: { blog: 'chiiki', article: 'fukuoka-kitakyushu', title: '北九州市LP' }
  };
  var CFG = Object.assign({}, FALLBACK_CFG, (window.ChiikiConciergeConfig || {}));
  var SUPABASE_URL = (CFG.supabaseUrl || '').replace(/\/+$/, '');
  var SUPABASE_KEY = CFG.supabaseAnonKey || '';
  var CHAT_ENDPOINT = CFG.chatEndpoint || 'shopify-chat';
  var PRODUCT_URL = CFG.productPageUrl || '/collections/all';
  var PAGE_CTX = CFG.pageContext || {};

  // 同日出荷バナー / 各種 CTA の出口リンク。デフォルトはトップ。
  // CFG.sameDayUrl で個別 LP 単位の上書きも可能。
  var SAME_DAY_URL = CFG.sameDayUrl || 'https://inkanlabo.com/';

  // UTM 自動付与。地域 LP 1,900 件の効果計測を GA4/Shopify Analytics で横並び比較できるようにする。
  //   utm_source   = chiiki-lp
  //   utm_medium   = concierge
  //   utm_campaign = <article-handle>（PAGE_CTX.article、無ければ unknown）
  //   utm_content  = 呼び出し側で指定（post-zip-cta など）
  function withUtm(url, content){
    if (!url) return url;
    try {
      var base = (typeof location !== 'undefined') ? location.origin : 'https://inkanlabo.com';
      var u = new URL(url, base);
      u.searchParams.set('utm_source', 'chiiki-lp');
      u.searchParams.set('utm_medium', 'concierge');
      u.searchParams.set('utm_campaign', String(PAGE_CTX.article || 'unknown'));
      u.searchParams.set('utm_content', content || 'post-zip-cta');
      return u.toString();
    } catch(_) {
      return url;
    }
  }
  // 画像更新時はファイル名末尾の vN を上げる（CDN/ブラウザの強い image cache を避けるため）
  var AVATAR_URL = CFG.avatarUrl || '//inkanlabo.com/cdn/shop/t/6/assets/cc-concierge-avatar-v3.jpg';
  var HEADER_AVATAR_SIZE = 48;

  // === 新方針 ===
  // 各メッセージごとにアバターを表示するのは煩雑なので、
  //   1) チャットUIのヘッダー左上に「コンシェルジュ顔写真」を 1 度だけフェードイン
  //   2) 各メッセージ行に元々挿入されている .cc-avatar は非表示にする
  // とする。Liquid snippet を編集せず JS だけで完結させ、Shopify の Liquid bytecode
  // キャッシュに引きずられないようにする。
  function buildHeaderAvatar(){
    var av = document.createElement('div');
    av.className = 'cc-header-avatar';
    av.setAttribute('aria-hidden', 'true');
    av.style.cssText = [
      'width:' + HEADER_AVATAR_SIZE + 'px',
      'height:' + HEADER_AVATAR_SIZE + 'px',
      'border-radius:50%',
      'background:#fff url(' + AVATAR_URL + ') center/cover no-repeat',
      'flex:0 0 auto',
      'border:1px solid rgba(0,0,0,.05)',
      'box-shadow:0 2px 6px rgba(0,0,0,.08)',
      'opacity:0',
      'transform:translateY(-4px) scale(.92)',
      'transition:opacity .9s ease, transform .9s ease'
    ].join(';');
    return av;
  }
  function setupHeaderAvatar(header){
    if (!header || header.dataset.headerAvatarApplied === '1') return;
    header.dataset.headerAvatarApplied = '1';
    header.style.display = 'flex';
    header.style.alignItems = 'center';
    header.style.justifyContent = 'flex-start';
    header.style.gap = '12px';
    header.style.textAlign = 'left';
    var av = buildHeaderAvatar();
    header.insertBefore(av, header.firstChild);
    // 次フレーム以降にフェードイン
    setTimeout(function(){
      av.style.opacity = '1';
      av.style.transform = 'translateY(0) scale(1)';
    }, 120);
  }
  function setupAllHeaderAvatars(root){
    var scope = root || document;
    if (!scope.querySelectorAll) return;
    var heads = scope.querySelectorAll('.conci-chat__header, .conci-mini__header');
    Array.prototype.forEach.call(heads, setupHeaderAvatar);
  }

  // 各メッセージ行のアバター（「印」or 旧画像）は非表示。
  // .cc-avatar を display:none にしておくだけ。flex コンテナなので残りのレイアウトには影響しない。
  function hideRowAvatar(el){
    if (!el || el.dataset.avatarHidden === '1') return;
    el.dataset.avatarHidden = '1';
    el.style.display = 'none';
  }
  function hideAllRowAvatars(root){
    var scope = root || document;
    if (!scope.querySelectorAll) return;
    var nodes = scope.querySelectorAll('.cc-avatar');
    Array.prototype.forEach.call(nodes, hideRowAvatar);
  }

  function bootstrapAvatars(){
    setupAllHeaderAvatars();
    hideAllRowAvatars();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapAvatars);
  } else {
    bootstrapAvatars();
  }
  if (typeof MutationObserver === 'function') {
    var avatarObserver = new MutationObserver(function(records){
      records.forEach(function(rec){
        rec.addedNodes && Array.prototype.forEach.call(rec.addedNodes, function(n){
          if (!n || n.nodeType !== 1) return;
          if (n.classList) {
            if (n.classList.contains('cc-avatar')) hideRowAvatar(n);
            if (n.classList.contains('conci-chat__header') || n.classList.contains('conci-mini__header')) setupHeaderAvatar(n);
          }
          if (n.querySelectorAll) {
            hideAllRowAvatars(n);
            setupAllHeaderAvatars(n);
          }
        });
      });
    });
    avatarObserver.observe(document.body || document.documentElement, { childList: true, subtree: true });
  }

  function safeTypeText(el, text, onDone){
    if (typeof window.typeText === 'function') {
      try { return window.typeText(el, text, onDone); } catch(_){ /* fallback */ }
    }
    if (!el) { if (onDone) onDone(); return; }
    el.textContent = '';
    var i = 0;
    var t = setInterval(function(){
      el.textContent = text.slice(0, ++i);
      if (i >= text.length) { clearInterval(t); if (onDone) onDone(); }
    }, 22);
  }

  // 配送ロジックは上の IIFE で定義された window.ChiikiDelivery を再利用する
  function getDeliveryText(zip){
    var api = window.ChiikiDelivery;
    if (!api || typeof api.buildText !== 'function') {
      return { greeting: '', body: '正確な納期は商品ページでご確認ください。', profile: { type: 'unknown' } };
    }
    var t = api.buildText(zip);
    var body = t.delivery ? (t.shipping + '\n' + t.delivery) : t.shipping;
    return { greeting: t.greeting || '', body: body, profile: t.profile };
  }

  var SCENARIO = {
    'scene:menu': {
      reply: 'どの場面で使う印鑑をお探しですか？',
      choices: [
        { label: '🏠 不動産の契約', goto: 'scene:jitsuin' },
        { label: '🚗 自動車の購入', goto: 'scene:jitsuin' },
        { label: '🏦 口座開設', goto: 'scene:ginko' },
        { label: '🏢 会社設立', goto: 'scene:houjin' },
        { label: '📝 自由に質問する', enterChat: 'general' }
      ]
    },
    'scene:jitsuin': {
      reply: '実印が必要なご用途ですね。市区町村に届け出る重要な印鑑です。\n\n推奨：サイズ 15〜18mm／フルネーム彫刻／耐久性の高い素材',
      choices: [
        { label: 'おすすめ実印を見る', href: '/collections/jitsuin' },
        { label: '素材を比較する', goto: 'material:menu' },
        { label: '北九州市の登録手続き', href: 'https://www.city.kitakyushu.lg.jp/', target: '_blank' },
        { label: '📝 自由に質問する', enterChat: 'jitsuin' }
      ]
    },
    'scene:ginko': {
      reply: '銀行印は金融機関の届出用です。\n\n推奨：サイズ 12〜15mm／苗字彫刻が一般的／実印と分けて長期保管',
      choices: [
        { label: 'おすすめ銀行印を見る', href: '/collections/ginkoin' },
        { label: '素材を比較する', goto: 'material:menu' },
        { label: '📝 自由に質問する', enterChat: 'ginko' }
      ]
    },
    'scene:houjin': {
      reply: '会社設立には法人印鑑セット（代表者印・角印・銀行印の3本）が必要です。登記時には代表者印が必須となります。',
      choices: [
        { label: '法人印鑑セットを見る', href: '/collections/houjin' },
        { label: '北九州市の登記情報', href: 'https://www.city.kitakyushu.lg.jp/', target: '_blank' },
        { label: '📝 自由に質問する', enterChat: 'houjin' }
      ]
    },
    'scene:other': { enterChat: 'scene_other' },

    'material:menu': {
      reply: 'どの素材に興味がありますか？素材で耐久性・押しやすさ・見た目が変わります。',
      choices: [
        { label: 'チタン（最強の耐久性）', goto: 'material:titanium' },
        { label: '黒水牛（人気No.1）', goto: 'material:kurosui' },
        { label: 'アカネ（リーズナブル）', goto: 'material:akane' },
        { label: '🤔 どれがいいか相談したい', goto: 'material:recommend' },
        { label: '📝 自由に質問する', enterChat: 'material' }
      ]
    },
    'material:titanium': {
      reply: 'チタンは最高の耐久性を誇り、半永久的にお使いいただけます。\n\n特徴：錆びない・欠けない・摩耗しない／捺印性も抜群／重厚感のある見た目',
      choices: [
        { label: 'チタン製の商品を見る', href: '/collections/titanium' },
        { label: '他の素材と比較', goto: 'material:menu' },
        { label: '📝 自由に質問する', enterChat: 'material/titanium' }
      ]
    },
    'material:kurosui': {
      reply: '黒水牛は当店人気No.1の素材です。\n\n特徴：耐久性が高く重厚感あり／押し心地がよく安定／コストパフォーマンスも良い',
      choices: [
        { label: '黒水牛の商品を見る', href: '/collections/kurosui' },
        { label: '他の素材と比較', goto: 'material:menu' },
        { label: '📝 自由に質問する', enterChat: 'material/kurosui' }
      ]
    },
    'material:akane': {
      reply: 'アカネは木製の手頃な印鑑素材です。\n\n特徴：温かみのある木目／リーズナブル／認印・銀行印に最適（実印には耐久性の高い素材を推奨）',
      choices: [
        { label: 'アカネの商品を見る', href: '/collections/akane' },
        { label: '他の素材と比較', goto: 'material:menu' },
        { label: '📝 自由に質問する', enterChat: 'material/akane' }
      ]
    },
    'material:recommend': {
      reply: 'お選びいただく印鑑のご用途を教えてください。',
      choices: [
        { label: '実印（重要な契約用）', goto: 'recommend:jitsuin' },
        { label: '銀行印', goto: 'recommend:ginko' },
        { label: '認印（日常使い）', goto: 'recommend:mitomein' }
      ]
    },
    'recommend:jitsuin': {
      reply: '実印には耐久性が最重要です。当店ではチタンか黒水牛をおすすめしています。\n・チタン：半永久的、初期投資は高め\n・黒水牛：コスパ重視、十分な耐久性',
      choices: [
        { label: 'おすすめ実印（チタン・黒水牛）', href: '/collections/jitsuin' },
        { label: '📝 自由に質問する', enterChat: 'recommend/jitsuin' }
      ]
    },
    'recommend:ginko': {
      reply: '銀行印は黒水牛が人気です。柘や水牛角でも十分実用的にお使いいただけます。',
      choices: [
        { label: 'おすすめ銀行印', href: '/collections/ginkoin' },
        { label: '📝 自由に質問する', enterChat: 'recommend/ginko' }
      ]
    },
    'recommend:mitomein': {
      reply: '認印は日常使いなのでアカネ・柘などの木製素材で十分です。リーズナブルにお選びいただけます。',
      choices: [
        { label: 'おすすめ認印', href: '/collections/mitomein' },
        { label: '📝 自由に質問する', enterChat: 'recommend/mitomein' }
      ]
    },

    'general:use': { goto: 'scene:menu' },
    'general:material': { goto: 'material:menu' },
    'general:delivery': {
      reply: '正確な納期は郵便番号でお調べいたします。↑画面上部の入力欄から郵便番号を入力してください。',
      choices: [
        { label: '↑ 入力欄に戻る', focusZip: true },
        { label: '📝 自由に質問する', enterChat: 'general/delivery' }
      ]
    }
  };

  function getHero(){ return document.getElementById('conci-hero'); }
  function getHeroBody(){ return document.getElementById('cc-body'); }
  function getZipInput(){ return document.getElementById('cc-zip'); }
  function getInputArea(){ return document.getElementById('cc-input-area'); }

  function appendBotMessage(body, text, onDone){
    var row = document.createElement('div');
    row.className = 'cc-msg';
    row.innerHTML = '<div class="cc-avatar" aria-hidden="true">印</div><div class="cc-text"></div>';
    body.appendChild(row);
    requestAnimationFrame(function(){ row.classList.add('visible'); });
    var textEl = row.querySelector('.cc-text');
    safeTypeText(textEl, text, function(){
      body.scrollTop = body.scrollHeight;
      if (onDone) onDone();
    });
  }
  function appendUserMessage(body, text){
    var row = document.createElement('div');
    row.className = 'cc-msg';
    row.style.flexDirection = 'row-reverse';
    row.innerHTML =
      '<div class="cc-text" style="background:var(--ochre-light);padding:8px 14px;border-radius:14px;color:var(--black);max-width:80%;margin-left:auto"></div>';
    body.appendChild(row);
    var textEl = row.querySelector('.cc-text');
    textEl.textContent = text;
    requestAnimationFrame(function(){ row.classList.add('visible'); });
    body.scrollTop = body.scrollHeight;
  }
  function appendChoiceButtons(body, choices, onPick){
    var wrap = document.createElement('div');
    wrap.className = 'cc-mini-btns visible';
    wrap.style.cssText = 'padding:6px 0 4px;display:flex;flex-wrap:wrap;gap:8px';
    choices.forEach(function(c){
      var node = document.createElement(c.href ? 'a' : 'button');
      node.textContent = c.label;
      node.style.cssText = 'display:inline-block;font-size:12px;font-weight:500;color:var(--ochre);background:var(--white);border:1px solid var(--ochre);border-radius:20px;padding:8px 16px;text-decoration:none;cursor:pointer;font-family:inherit';
      if (c.href) {
        node.href = c.href;
        if (c.target) node.target = c.target;
        if (c.target === '_blank') node.rel = 'noopener';
      } else {
        node.type = 'button';
      }
      node.addEventListener('click', function(e){
        if (!c.href) e.preventDefault();
        if (onPick) onPick(c, node, wrap);
      });
      wrap.appendChild(node);
    });
    body.appendChild(wrap);
    body.scrollTop = body.scrollHeight;
    return wrap;
  }

  var STATE = { ctx: { trail: [] }, hasOpened: false };

  function openHero(){
    var hero = getHero();
    if (!hero) return;
    hero.scrollIntoView({ behavior:'smooth', block:'center' });
  }

  function ensureHeroEntry(){
    var inputArea = getInputArea();
    if (inputArea && !inputArea.classList.contains('visible')) {
      var msg1 = document.getElementById('cc-msg1');
      var msg2 = document.getElementById('cc-msg2');
      if (msg1) msg1.classList.add('visible');
      if (msg2) msg2.classList.add('visible');
      var t1 = document.getElementById('cc-text1');
      var t2 = document.getElementById('cc-text2');
      if (t1 && !t1.textContent) t1.textContent = 'こんにちは。';
      if (t2 && !t2.textContent) t2.textContent = 'よろしければ、下記から正確な納期をお伝えいたします。';
      inputArea.classList.add('visible');
    }
  }

  function runNode(nodeId){
    var node = SCENARIO[nodeId];
    if (!node) return;
    if (node.enterChat) { enterFreeChat(node.enterChat); return; }
    if (node.goto && !node.reply && !node.choices) { runNode(node.goto); return; }

    var body = getHeroBody();
    if (!body) return;
    STATE.ctx.trail.push({ nodeId: nodeId });

    var reply = typeof node.reply === 'function' ? node.reply(STATE.ctx) : node.reply;
    if (!reply) {
      if (node.choices) renderChoices(body, node.choices);
      return;
    }
    appendBotMessage(body, reply, function(){
      if (node.choices) {
        setTimeout(function(){ renderChoices(body, node.choices); }, 280);
      }
    });
  }

  function renderChoices(body, choices){
    appendChoiceButtons(body, choices, function(choice, btnEl, wrap){
      appendUserMessage(body, choice.label);
      STATE.ctx.trail.push({ label: choice.label });
      Array.prototype.forEach.call(wrap.children, function(b){ b.style.opacity='0.5'; b.style.pointerEvents='none'; });

      if (choice.focusZip) {
        var z = getZipInput();
        if (z) { z.focus(); z.scrollIntoView({behavior:'smooth', block:'center'}); }
        return;
      }
      if (choice.enterChat) { enterFreeChat(choice.enterChat); return; }
      if (choice.goto) { setTimeout(function(){ runNode(choice.goto); }, 200); return; }
    });
  }

  // 郵便番号 → 都市名（市区町村）を zipcloud から取得。タイムアウト 1.5s。
  // 失敗時は null を返す（挨拶は省略）。
  function fetchCityName(digits){
    if (!digits || digits.length < 7 || typeof fetch !== 'function') {
      return Promise.resolve(null);
    }
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = setTimeout(function(){ if (ctrl) ctrl.abort(); }, 1500);
    var url = 'https://zipcloud.ibsnet.co.jp/api/search?zipcode=' + encodeURIComponent(digits);
    return fetch(url, ctrl ? { signal: ctrl.signal } : {})
      .then(function(r){ return r.ok ? r.json() : null; })
      .then(function(json){
        clearTimeout(timer);
        if (!json || !Array.isArray(json.results) || !json.results.length) return null;
        var hit = json.results[0];
        // 政令指定都市は address2 = "札幌市中央区" のように区まで含む。そのまま使う。
        return (hit.address2 || hit.address1 || '').trim() || null;
      })
      .catch(function(){ clearTimeout(timer); return null; });
  }

  function handleZipSubmit(){
    var zipEl = getZipInput();
    if (!zipEl) return;
    var raw = (zipEl.value || '').trim();
    var api = window.ChiikiDelivery;
    var digits = api && api.normalizeZip ? api.normalizeZip(raw) : raw.replace(/\D/g, '');
    if (digits.length < 7) { zipEl.focus(); return; }
    if (STATE.hasOpened) return;
    STATE.hasOpened = true;

    var body = getHeroBody();
    var inputArea = getInputArea();
    appendUserMessage(body, '〒 ' + raw);

    var delivery = getDeliveryText(digits);
    STATE.ctx.zip = digits;
    STATE.ctx.deliveryAnswer = delivery.body;

    document.dispatchEvent(new CustomEvent('chiiki:postal-changed', { detail: { postal: raw } }));

    var afterDelivery = function(){
      setTimeout(function(){
        // 配送案内のあとは「即日発送の印鑑はこちら」1択に統一。
        // 離島も「当日出荷自体は可能」なので同じ導線で OK。
        renderChoices(body, [
          { label: '🚚 即日発送の印鑑はこちら', href: withUtm(SAME_DAY_URL, 'post-zip-cta') }
        ]);
      }, 380);
    };

    var showFlow = function(greeting){
      if (greeting) {
        appendBotMessage(body, greeting, function(){
          setTimeout(function(){
            appendBotMessage(body, delivery.body, afterDelivery);
          }, 320);
        });
      } else {
        appendBotMessage(body, delivery.body, afterDelivery);
      }
    };

    // 入力直後の即返しは機械的に見えるため、最初の応答までに 2 秒の「考えている時間」を挟む
    var REPLY_DELAY_MS = 2000;
    if (delivery.greeting) {
      setTimeout(function(){ showFlow(delivery.greeting); }, REPLY_DELAY_MS);
    } else {
      // 本土：zipcloud から市区町村名を取得（最大1.5s）。
      // ディレイと並列で fetch して、最低 2 秒は待ってから出す。
      var t0 = Date.now();
      fetchCityName(digits).then(function(city){
        var greeting = city ? (city + 'ですね。') : '';
        STATE.ctx.cityName = city || null;
        var elapsed = Date.now() - t0;
        var wait = Math.max(0, REPLY_DELAY_MS - elapsed);
        setTimeout(function(){ showFlow(greeting); }, wait);
      });
    }

    if (inputArea) {
      inputArea.style.transition = 'opacity .4s ease-out, max-height .4s ease-out';
      inputArea.style.maxHeight = inputArea.offsetHeight + 'px';
      requestAnimationFrame(function(){
        inputArea.style.maxHeight = '0';
        inputArea.style.opacity = '0';
        inputArea.style.overflow = 'hidden';
        inputArea.style.padding = '0 20px';
      });
    }
  }

  function callChat(action, payload){
    if (!SUPABASE_URL || !SUPABASE_KEY) {
      return Promise.reject(new Error('Supabase 設定がありません'));
    }
    var url = SUPABASE_URL + '/functions/v1/' + CHAT_ENDPOINT;
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      },
      body: JSON.stringify(Object.assign({ action: action }, payload || {}))
    }).then(function(r){
      if (!r.ok) return r.text().then(function(t){ throw new Error('chat API '+r.status+': '+t.slice(0,200)); });
      return r.json();
    });
  }

  function buildGuideContext(intent){
    var trail = STATE.ctx.trail.map(function(s){ return s.label || s.nodeId; }).filter(Boolean).join(' → ');
    var parts = ['ページ: 北九州市LP'];
    if (PAGE_CTX.title) parts.push('記事: ' + PAGE_CTX.title);
    if (STATE.ctx.zip) parts.push('郵便番号: ' + STATE.ctx.zip);
    if (intent) parts.push('現在の関心: ' + intent);
    if (trail) parts.push('これまでの選択: ' + trail);
    return parts.join(' / ');
  }

  function enterFreeChat(intent){
    var body = getHeroBody();
    if (!body) return;
    appendBotMessage(body, '承知しました。ご質問を承ります。下の入力欄からお気軽にどうぞ。', function(){
      renderFreeChatInput(body, intent);
      if (!SUPABASE_URL || !SUPABASE_KEY) {
        appendBotMessage(body, '※ ただいま AI コンシェルジュに接続できません。お電話または商品ページからもお気軽にお問い合わせください。');
        return;
      }
      var guideCtx = buildGuideContext(intent);
      callChat('start', { guide_context: guideCtx })
        .then(function(res){ if (res && res.session_id) STATE.ctx.sessionId = res.session_id; })
        .catch(function(err){
          console.warn('[chiiki] chat start failed:', err);
          appendBotMessage(body, '※ AI コンシェルジュへの接続に失敗しました。お手数ですが、もう一度お試しください。');
        });
    });
  }

  function renderFreeChatInput(body, intent){
    var existing = body.querySelector('[data-cc-chat-form]');
    if (existing) return;
    var form = document.createElement('form');
    form.setAttribute('data-cc-chat-form', '');
    form.style.cssText = 'display:flex;gap:6px;padding:10px 0 0;width:100%';
    form.innerHTML =
      '<input type="text" placeholder="ご質問を入力" '
      + 'style="flex:1;padding:10px 12px;border:1px solid var(--brown);border-radius:8px;font-size:14px;font-family:inherit;outline:none">'
      + '<button type="submit" style="padding:10px 18px;background:var(--ochre);color:var(--white);border:none;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit">送信</button>';
    body.appendChild(form);
    var input = form.querySelector('input');
    setTimeout(function(){ input.focus(); }, 200);

    form.addEventListener('submit', function(e){
      e.preventDefault();
      var text = (input.value || '').trim();
      if (!text) return;
      input.value = '';
      appendUserMessage(body, text);

      if (!STATE.ctx.sessionId) {
        appendBotMessage(body, '※ セッションが切れたようです。ページを再読み込みしてください。');
        return;
      }
      var thinking = document.createElement('div');
      thinking.className = 'cc-msg visible';
      thinking.innerHTML = '<div class="cc-avatar" aria-hidden="true">印</div><div class="cc-text" style="opacity:.6">考え中…</div>';
      body.appendChild(thinking);
      body.scrollTop = body.scrollHeight;

      callChat('message', { session_id: STATE.ctx.sessionId, message: text })
        .then(function(res){
          thinking.remove();
          appendBotMessage(body, (res && res.reply) || 'ご質問ありがとうございます。少々お待ちください。');
        })
        .catch(function(err){
          thinking.remove();
          console.warn('[chiiki] chat message failed:', err);
          appendBotMessage(body, '※ 通信に失敗しました。お手数ですが、もう一度お試しください。');
        });
    });
  }

  function resolveIntentNode(intent, label){
    if (intent === 'jitsuin') return 'scene:jitsuin';
    if (intent === 'ginko') return 'scene:ginko';
    if (intent === 'houjin') return 'scene:houjin';
    if (intent === 'other') return 'scene:other';
    if (intent === 'recommend') return 'material:recommend';
    if (intent === 'material') {
      if (label.indexOf('チタン') >= 0) return 'material:titanium';
      if (label.indexOf('黒水牛') >= 0) return 'material:kurosui';
      if (label.indexOf('アカネ') >= 0) return 'material:akane';
      return 'material:menu';
    }
    if (intent === 'general') {
      if (label.indexOf('用途') >= 0) return 'general:use';
      if (label.indexOf('素材') >= 0) return 'general:material';
      if (label.indexOf('納期') >= 0) return 'general:delivery';
      return 'scene:menu';
    }
    return 'scene:menu';
  }

  function bindCapture(){
    document.addEventListener('click', function(e){
      var sb = e.target.closest('.cc-submit');
      if (sb) {
        e.preventDefault();
        e.stopImmediatePropagation();
        handleZipSubmit();
        return;
      }
      var intentLink = e.target.closest('a[href="#conci-hero"][data-cc-intent], [data-cc-intent]');
      if (intentLink) {
        var intent = intentLink.getAttribute('data-cc-intent') || '';
        if (!intent) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        var label = intentLink.getAttribute('data-cc-label') || (intentLink.textContent || '').trim();
        openHero();
        setTimeout(function(){
          ensureHeroEntry();
          if (!STATE.hasOpened) {
            STATE.hasOpened = true;
            var inputArea = getInputArea();
            if (inputArea) inputArea.style.display = 'none';
          }
          var body = getHeroBody();
          if (body) appendUserMessage(body, label);
          STATE.ctx.trail.push({ label: label });
          var nodeId = resolveIntentNode(intent, label);
          runNode(nodeId);
        }, 350);
      }
    }, true);

    document.addEventListener('keydown', function(e){
      if (e.key !== 'Enter') return;
      var z = getZipInput();
      if (!z || document.activeElement !== z) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      handleZipSubmit();
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCapture);
  } else {
    bindCapture();
  }
})();
